import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    linkedSignal,
    model,
    output,
    signal,
    untracked,
    viewChild,
} from "@angular/core";
import { HttpRequestOptions } from "../../dataaccess/data.access";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { CalendarService } from "../../modules/home/calendar/calendar.service";
import { CalendarData } from "../../modules/home/calendar/CalendarData";
import { OptBean, VisualizationType } from "../../modules/s25-opt/s25.opt.component";
import { S25OptModule } from "../../modules/s25-opt/s25.opt.module";
import { OptUtil, OptUtilI } from "../../modules/s25-opt/s25.opt.util";
import { Search } from "../../modules/s25-ql/search";
import { DropDownItem } from "../../pojo/DropDownItem";
import { HasItemType, Item } from "../../pojo/Item";
import { Refreshable } from "../../pojo/Util";
import { EventService } from "../../services/event.service";
import { EmbeddingUtil } from "../../util/embedding.util";
import { s25Resource } from "../../util/s25.resource";
import { Calendar, type TempSearchFn } from "./calendar.component";
import { S25CalendarStore } from "./calendar.store";
import { S25Const } from "../../util/s25-const";
import { NetworkHealthService } from "../../services/network.health.service";
import { S25LoadingInlineStaticComponent } from "../../modules/s25-loading/s25.loading.inline.static.component";
import { S25IconButtonComponent } from "../s25.icon.button.component";
import { S25Util } from "../../util/s25-util";
import { EventSummaryService } from "../../modules/s25-swarm-schedule/s25.event.summary.service";

type OptBeanGenerator = (refreshF: any, runTimeParams: Partial<OptBean>) => Promise<OptUtilI>;

export interface CalendarOptModelBean {
    itemId?: number;
    itemTypeId?: Item.Id;
    searchQuery?: string;
    // TODO: delete this when qlSearch is moved to Angular
    refreshListeners?: Array<(beanUpdates: Partial<Omit<CalendarOptModelBean, "refreshListeners">>) => void>;
    /**
     * This is currently being passed in from qlSearch as an alternative way to get the search query.
     * TODO: consider merging the two
     */
    tempSearchFn?: () => Promise<{ invalidSearch: boolean; searchQuery: string }>;
    getdata?: (params: CalendarService.DataParams, httpOptions?: HttpRequestOptions) => Promise<{ root: CalendarData }>;
    messages?: { loading: string; message: string };
    isauth?: { value: boolean };
    optBean?: OptBean;
    optBeanRunTime?: {
        date?: Date;
        searchContext?: string;
        itemId?: number;
        itemTypeId?: Item.Id;
        compsubject?: string;
        helpTopic?: string;
        selectedViz?: VisualizationType;
        hasListVisualization?: boolean;
        hasCalendarVisualization?: boolean;
        hasAvailabilityVisualization?: boolean;
        hasAvailabilityWeeklyVisualization?: boolean;
        hasRelatedLocationsCheckbox?: boolean;
    };
}

@TypeManagerDecorator("s25-ng-calendar-opt")
@Component({
    selector: "s25-ng-calendar-opt",
    standalone: true,
    template: `
        @if (inApp && sourceOptBean.resource.isLoading()) {
            <s25-ng-loading-inline-static />
        }

        @if (optBean()) {
            <s25-ng-opt
                [modelBean]="optBean()"
                (modelValueChange)="updateOptBean($event)"
                (dateChange)="updateOptBean()"
                (endDateChange)="updateOptBean()"
                (dateViewChange)="updateOptBean()"
                (daysChange)="updateOptBean()"
                (includeRequestedChange)="updateOptBean()"
                (refreshed)="updateOptBean()"
                (modeChange)="updateOptBean()"
                (snapToGridChange)="updateOptBean()"
                (utilizationViewChange)="updateOptBean()"
                (queryChange)="updateOptBean()"
                (tabChange)="onTabChange($event)"
                (columnChooserChange)="updateOptBean()"
                (editModeChange)="updateOptBean()"
                (initialized)="onOptBarInitialized()"
            />
            @if (!sourceOptBean.resource.error()) {
                <s25-ng-calendar
                    class="ngSize100"
                    [isLoading]="!optBarInitialized()"
                    [comptype]="comptype()"
                    [compsubject]="compsubject()"
                    [searchQuery]="searchQuery()"
                    [date]="$safeNavigationMigration(optBean()?.date)"
                    [dataFn]="$safeNavigationMigration(modelBean()?.getdata)"
                    [tempSearchFn]="tempSearchFn()"
                    [multiQueryStr]="$safeNavigationMigration(optBean()?.multiQueryStr)"
                    [useCache]="$safeNavigationMigration(optBean()?.useCache)"
                    [includeAddtl]="$safeNavigationMigration(optBean()?.includeAddtl)"
                    [showRelatedEvents]="$safeNavigationMigration(optBean()?.showRelatedEvents)"
                    [showRelatedLocations]="$safeNavigationMigration(optBean()?.showRelatedLocations)"
                    [datePropertyValue]="$safeNavigationMigration(optBean()?.datePropertyValue)"
                    [endDate]="$safeNavigationMigration(optBean()?.endDate)"
                    [chosenVal]="$safeNavigationMigration(optBean()?.chosen?.obj?.val)"
                    [showBlackouts]="$safeNavigationMigration(optBean()?.showBlackouts)"
                    (updated)="setLastUpdateDate($event)"
                    (previousPage)="previousPage()"
                    (nextPage)="nextPage()"
                />
            }
        }

        @if (errorMessage()) {
            <div class="NoInformation">
                <span>
                    @if (inApp && !sourceOptBean.resource.isLoading()) {
                        <s25-ng-icon-button [type]="'refresh'" [ariaLabel]="'Retry'" (clicked)="retry($event)" />
                    }
                    {{ errorMessage() }}
                </span>
            </div>
        }
    `,
    styles: `
        s25-ng-loading-inline-static {
            display: block;
            margin: auto;
            width: fit-content;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [Calendar, S25OptModule, S25LoadingInlineStaticComponent, S25IconButtonComponent],
    providers: [S25CalendarStore],
})
export class CalendarWithOptions implements Refreshable, HasItemType {
    readonly initialCompType = input.required<OptUtil.CompType>({
        alias: "comptype",
    });
    readonly initialCompSubject = input.required<Item.Subject>({
        alias: "compsubject",
    });
    readonly modelBean = model<CalendarOptModelBean>();
    readonly searchQuery = input<string>();
    readonly tempSearchFn = input<TempSearchFn>();
    readonly tabChange = output<Search.Tab>();

    readonly calendar = viewChild(Calendar);

    private readonly store = inject(S25CalendarStore);
    private readonly elementRef = inject(ElementRef);
    private readonly embeddingUtil = EmbeddingUtil.getSingleton();

    readonly inApp = S25Const.inApp;

    private readonly optBarEverInitialized = signal(false);
    protected readonly optBarInitialized = computed(
        () => this.optBarEverInitialized() && !this.sourceOptBean.resource.isLoading() && !!this.sourceOptBean.value(),
    );
    private readonly providedOptBeanRunTime = computed(() => this.modelBean()?.optBeanRunTime);
    protected readonly sourceOptBean = s25Resource({
        params: () => ({
            optBeanRunTime: {
                ...(this.providedOptBeanRunTime() || {}),
                compsubject: this.compsubject(),
                ...(this.comptype() === "calendar"
                    ? {
                          helpTopic: {
                              event: "view_myevents_calendar",
                              location: "view_loc_calendar",
                              organization: "view_org_calendar",
                              resource: "view_res_calendar",
                          }[<string>this.compsubject()],
                      }
                    : {}),
            },
            optBeanGenerator: this.OptBeanGenerators[this.comptype()] ?? undefined,
        }),
        loader: ({ params }): Promise<OptUtilI> =>
            params
                .optBeanGenerator?.(() => this.calendar()?.refresh(), params.optBeanRunTime)
                ?.catch((err) => {
                    if (this.inApp) {
                        const message = !untracked(NetworkHealthService.healthy)
                            ? "Unable to load Calendar due to poor network conditions. Please check your connection and try again."
                            : "Unable to load Calendar. Please try again later.";
                        throw new Error(message, { cause: err });
                    } else {
                        throw err;
                    }
                }) ?? Promise.reject(undefined),
    });
    readonly compsubject = linkedSignal(() => this.initialCompSubject());
    readonly comptype = linkedSignal(() => this.initialCompType());
    // jenky way of overriding the chosen given to us by the OptBeanGenerator
    readonly chosen = signal<{ obj: DropDownItem } | undefined>(undefined);
    /** This is the optBean for s25-ng-opt that will be mutated >:( */
    // TODO: update s25-ng-opt to accept granular inputs so we can abandon the omni-object
    // WARNING: using optBean in any signals that can indirectly update optBean will cause infinite loops
    readonly optBean = linkedSignal<OptBean | undefined>(
        () =>
            this.sourceOptBean.value() && {
                ...this.sourceOptBean.value()!,
                ...untracked(() => (this.chosen() ? { chosen: this.chosen() } : {})),
            },
    );
    readonly errorMessage = computed(() => this.sourceOptBean.resource.error()?.message);

    public get itemTypeId() {
        return S25Const.itemName2Id[this.store.compsubject()];
    }

    constructor() {
        // effects that set signals are a smell, but this is necessary because our primary way of detecting
        // changes is through the omni-object
        effect(() => {
            if (this.optBean()?.comptype) {
                this.comptype.set(this.optBean()!.comptype!);
            }
        });
        effect(() => {
            if (this.optBean()?.chosen) {
                this.chosen.set(this.optBean()!.chosen!);
            }
        });
        effect(() => {
            if (this.optBean()?.compsubject) {
                this.compsubject.set(this.optBean()!.compsubject!);
            }
        });
        effect(() => {
            if (this.sourceOptBean.value()) {
                this.embeddingUtil?.registerHeightManager(this.elementRef.nativeElement, null, 120, 1000);
            }
        });

        effect(() => this.store.setCompsubject(this.compsubject()));
        effect(() => this.store.setComptype(this.comptype()));
        effect(() => this.store.setModelBean(this.modelBean()));
        effect(() => this.store.setOptBean(this.optBean()));
    }

    protected onOptBarInitialized(): void {
        this.optBarEverInitialized.set(true);
    }

    public onTabChange(newTab: Search.Tab) {
        this.tabChange.emit(newTab);
    }

    // Query the rendered toggle directly to avoid a circular import on S25OptComponent (NG view queries
    // cannot reach into the <s25-ng-opt> child's template anyway).
    public focusViewButton(tab: VisualizationType): boolean {
        const button = this.elementRef.nativeElement.querySelector(
            `s25-ng-opt-visualization-buttons button[data-view="${tab}"]`,
        ) as HTMLButtonElement | null;
        if (!button) return false;
        button.focus();
        return true;
    }

    public updateOptBean(optBean?: OptBean | undefined) {
        // trigger change detection if something changes lol
        const sourceBean = optBean ?? this.optBean();
        this.optBean.set(sourceBean ? { ...sourceBean } : undefined);
    }

    public previousPage() {
        this.optBean()?.updateDateArrow?.("back");
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    public nextPage() {
        this.optBean()?.updateDateArrow?.("forward");
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    OptBeanGenerators: { [key in OptUtil.CompType]: OptBeanGenerator | undefined } = {
        home: OptUtil.formHomeCalendarBean,
        cal_event: async (refreshF, params) => {
            const summary = await EventSummaryService.getSummary(params.itemId!);
            const firstDate = summary.summary.prof
                ?.map((p) => S25Util.date.parseDropTZ(p.init_start_dt))
                .reduce((min, curr) => (curr < min ? curr : min));
            return OptUtil.formEventDetailsCalendarBean(refreshF, {
                ...params,
                date: params.date ?? firstDate ?? new Date(),
            });
        },
        cal_location: OptUtil.formLocationDetailsCalendarBean,
        cal_resource: OptUtil.formResourceDetailsCalendarBean,
        cal_organization: OptUtil.formOrganizationDetailsCalendarBean,
        cal_contact: OptUtil.formContactDetailsCalendarBean,
        cal_task: undefined,
        cal_task_search: OptUtil.formTaskSearchCalendarBean,
        calendar: OptUtil.formSearchCalendarBean,
    };

    public setLastUpdateDate(date: OptUtilI["lastupdate"]) {
        // TODO: ideally, this would be passed in as a property to s25-ng-opt,
        //  or the bean would be a computed signal
        this.optBean.update((bean) => ({
            ...bean!,
            lastupdate: date,
        }));
    }

    refresh() {
        this.calendar()?.refresh();
    }

    retry(event?: Event) {
        event?.stopPropagation();
        this.sourceOptBean.resource.reload();
    }
}
